<template lang='pug'>
  div
    v-toolbar(dense).align-toolbar-buttons
      bionic-metrics-create-load-test(v-show='!selected.length' ref='createLoadTest')
      v-tooltip(right v-if='selected.length')
        template(v-slot:activator='{on}')
          v-btn(v-on='on' @click='deleteSelected()' text icon color='error')
            v-icon delete
        span Delete
      v-spacer
      v-spacer
      v-text-field(v-model='search' ref='search' placeholder='Search ("/" to focus)' hide-details prepend-inner-icon='search' clearable single-line)
    table-list(:headers='headers'
      :items='loadTests'
      :total='total'
      :init-options='tableOptions'
      :loading='loading'
      v-model='selected'
      @update='update')
      template(v-slot:empty)
        v-container(v-if='!loading && !loadTests.length')
          v-row
            v-col(align='center' justify='center')
              .text-h5.grey--text No Load Tests Defined
              v-btn(text color='accent' @click='openCreateLoadTest') Create Load Test
      template(v-slot:row='{item, index, selected, expanded}')
        td
          v-row(align='center')
            v-tooltip(bottom)
              template(v-slot:activator='{on}')
                v-btn.ma-0(v-on='on' text icon :to='`/load-test/${item.guid}`')
                  v-icon mode_edit
              span Edit
        td
          router-link(:to='`/load-test/${item.guid}`') {{item.name}}
        td {{item.description | truncate(40)}}
        td
          time-ago(:date='item.createdAt')
        td
          time-ago(:date='item.updatedAt')
        td
          v-row(justify='end')
            v-menu(offset-y left)
              template(v-slot:activator='{on}' )
                v-btn(text icon v-on='on' @click.native.stop='')
                  v-icon more_vert
              v-list
                v-list-item(@click='deleteLoadTest(item)')
                  v-list-item-title
                    | Delete
        // </tr> (Gets put inside tr)
      template(v-slot:item='{item}')
        v-row(no-gutters)
          v-col(cols='12').text-body-1.pa-3
            .text-subtitle-1 {{item.name}}
            v-row(no-gutters)
              v-col.text-body-2(cols='4' sm='3') Description:
              v-col {{item.description | truncate(60)}}
            v-row(no-gutters)
              v-col.text-body-2(cols='4' sm='3') Created:
              v-col
                time-ago-reverse(:date='item.createdAt')
            v-row(no-gutters)
              v-col.text-body-2(cols='4' sm='3') Updated:
              v-col
                time-ago-reverse(:date='item.updatedAt')
            v-row(justify='end')
              v-tooltip(bottom)
                template(v-slot:activator='{on}')
                  v-btn(v-on='on' text icon :to='`/load-test/${item.guid}`')
                    v-icon mode_edit
                span Edit
              v-menu(offset-y @click.native.stop='' left)
                template(v-slot:activator='{on}')
                  v-btn(text icon v-on='on')
                    v-icon more_vert
                v-list
                  v-list-item(@click='deleteLoadTest(item)')
                    v-list-item-title
                      | Delete LoadTest
        v-divider
</template>

<script>
import {debounce} from 'lodash';
import {mapGetters, mapMutations} from 'vuex';
import TableMixin from '@/mixins/table.mixin';
import FocusMixin from '@/mixins/focus.mixin';
import UtilMixin from '@/mixins/util.mixin';
import Notifications from '@/services/notification.service';
import LoadTest from '@/api/loadTest';
import TableList from '@/components/TableList';
import DisplayTags from '@/components/DisplayTags';
import FilterButton from '@/components/FilterButton';
import Tags from '@/components/Tags';
import TimeAgo from '@/components/TimeAgo';
import BionicMetricsCreateLoadTest from '@/components/CreateLoadTest';
import TimeAgoReverse from '@/components/TimeAgoReverse';

export default {
  name: 'BionicMetricsLoadTests',
  components: {
    TimeAgoReverse,
    BionicMetricsCreateLoadTest,
    TimeAgo,
    Tags,
    FilterButton,
    DisplayTags,
    TableList
  },
  mixins: [TableMixin, FocusMixin, UtilMixin],
  computed: {
    ...mapGetters({
      user: 'getUser',
      tableOptions: 'getLoadTestTableOptions'
    })
  },
  methods: {
    async load() {
      this.loading = true;
      try {
        const {list, total} = await LoadTest.list({
          skip: this.skip,
          limit: this.limit,
          sort: this.sort,
          search: this.search
        });
        this.loadTests = list;
        this.total = total;
      } catch (error) {
        console.error(`Problem getting list of load-tests: ${error.message}`);
        Notifications.errorToast('Error Loading LoadTests', {duration: 30000, retry: this.load});
      }
      this.loading = false;
    },
    async removeLoadTest(guid) {
      await LoadTest.remove(guid);
      const i = this.loadTests.findIndex((am) => am.guid === guid);
      this.loadTests.splice(i, 1);
    },
    async deleteLoadTest(amacro) {
      if (!(await Notifications.confirm('Delete', `Are you sure you want to delete <strong>${amacro.name}</strong>?`))) return;
      await this.removeLoadTest(amacro.guid);
      Notifications.toast('Deleted');
    },
    async deleteSelected() {
      if (!(await Notifications.confirm('Delete', `Are you sure you want to delete <strong>${this.selected.length}</strong> load tests?`))) return;
      const all = [];
      for (const loadTest of this.selected) {
        all.push(this.removeLoadTest(loadTest.guid));
      }
      await Promise.all(all);
      this.selected = [];
      Notifications.toast('Deleted');
    },
    openCreateLoadTest() {
      this.$refs.createLoadTest.open();
    },
    ...mapMutations({
      setTableOptions: 'setLoadTestTableOptions'
    })
  },
  watch: {
    // eslint-disable-next-line
    search: debounce(function () {
      this.load();
    }, 250)
  },
  data() {
    return {
      disableCreateButtons: false,
      search: '',
      selected: [],
      headers: [
        {text: 'Actions', value: 'actions', sortable: false},
        {text: 'Name', value: 'name'},
        {text: 'Description', value: 'description'},
        {text: 'Created', value: 'createdAt'},
        {text: 'Updated', value: 'updatedAt'},
        {text: '', value: 'moreActions', sortable: false}
      ],
      loadTests: [],
      total: 0,
      skip: 0,
      limit: 10,
      sort: {name: 1},
      defaultSort: {name: 1},
      loading: false
    };
  }
};
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
</style>
